import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, catchError, debounceTime, distinctUntilChanged, filter, finalize, firstValueFrom, map, of, take, tap } from 'rxjs';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import { LegalPersonalityEnum, LegalPersonalityIdEnum, getLegalPersonalityDescriptionByValue } from 'src/app/domain/enums/legal-personality.enum';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { getRegimeTypeDescriptionByValue } from 'src/app/domain/registration/enums/regime-type.enum';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { IPartner } from 'src/app/domain/registration/models/IPartner';
import { AbstractCrudCES } from 'src/app/shared/components/crud/abstract-crud-ces';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CES_CONFIG_FIELDS } from '../crud-config';
import { PartnerService } from '../crud-service';
import { BusinessHoursComponent } from './business-hours/business-hours.component';
import { FormDataCreate } from './formDataBuild/form-data-create';
import { FormDataUpdate } from './formDataBuild/form-data-update';
import { PermissionService } from 'src/app/shared/permission.service';
import { CustomerDataDialogComponent } from '../customer-data-dialog/customer-data-dialog.component';
import { DialogMerchantComponent } from './dialog-merchant/dialog-merchant.component';
import { IMerchantPartnersFormData } from './dialog-merchant/dialog-merchant-partners-interface';
import { ICreateMerchantRequest } from 'src/app/domain/registration/models/merchants/ICreateMerchantRequest';
import { formatDateForAPI, formatPhoneForAPI, formatPhoneInternational } from 'src/app/shared/utils/api-formatters';
import { MerchantService } from '../../merchants/crud-service';
import { MerchantValidatorService } from './dialog-merchant/merchant-validator.service';

@Component({
  selector: 'app-partner-ces',
  templateUrl: './crud-ces.component.html',
  styleUrls: ['./crud-ces.component.sass'],
})
export class PartnerCESComponent
  extends AbstractCrudCES<IPartner>
  implements OnInit, OnDestroy {
  consultantSelected: any;
  partnerSelected: any;
  representativeSelected: any;

  showModalMerchant: boolean = false;
  resultModal: IMerchantPartnersFormData;
  existingMerchant:boolean = false;
  private isLoadingMerchantData: boolean = false;

  private isConsultantSub: Subscription | undefined;
  private isPartnerSub: Subscription | undefined;
  private isRepresentativeSub: Subscription | undefined;
  private isCounterSub: Subscription | undefined;

  back() {
    this.router.navigate(['partners'], { queryParams: { preserveFilter: 'true' } });
  }

  override config: IFieldConfig[] = CES_CONFIG_FIELDS;
  public filteredCities: any[] = [];
  private legalPersonalitySub: Subscription | undefined;

  setFields() {
    this.form.patchValue({
      ...this.data,
      legalPersonality: getLegalPersonalityDescriptionByValue(
        this.data.legalPersonality
      ),
      regime: getRegimeTypeDescriptionByValue(this.data.regime),
      consultant: this.data?.consultant?.name || this.data?.consultant?.companyName,
      partner: this.data?.partner?.name || this.data?.partner?.companyName,
      representative: this.data?.representative?.name || this.data?.representative?.companyName,
    });

    if (this.data?.isRepresentative) {
      this.partnerSelected = this.data?.partner;
      this.consultantSelected = this.data?.consultant;
    }

    this.onChangeState(this.data.state);
    this.form.get('city')?.setValue(this.data.city);

    this.updateCommissionFieldState('consultantCommission', this.form.get('isConsultant')?.value);
    this.updateCommissionFieldState('partnerCommission', this.form.get('isPartner')?.value);
    this.updateCommissionFieldState('representativeCommission', this.form.get('isRepresentative')?.value);
    this.updateCommissionFieldState('counterCommission', this.form.get('isCounter')?.value);

    if (this.data?.id) {
      this.loadMerchantData(this.data.id);
    }
  }

  private loadMerchantData(partnerId: string): void {
    this.isLoadingMerchantData = true; 

    this.merchantService.getMerchantByPartnerId(partnerId).pipe(
      take(1),
      tap((merchant: any) => {
        if (merchant) {
          this.resultModal = this.mapMerchantToModalData(merchant);
          this.existingMerchant = true;
          this.form.patchValue({ isMerchant: true });
        }
      }),
      catchError((error) => {
        return of(null);
      }),
      finalize(() => {
        this.isLoadingMerchantData = false; 
      })
    ).subscribe();
  }

  private mapMerchantToModalData(merchant: any): IMerchantPartnersFormData {
    return {
      chavePix: merchant.pixKey,
      celularFinanceiro: merchant.financialPhone,
      emailFinanceiro: merchant.financialEmail,
      responsavelNome: merchant.responsibleName,
      responsavelCpf: merchant.responsibleCpf,
      responsavelRg: merchant.responsibleRg,
      responsavelNacionalidade: merchant.responsibleNationality,
      responsavelCelular: merchant.responsiblePhone,
      responsavelEmail: merchant.responsibleEmail,
      responsavelDataNascimento: merchant.responsibleBirthDate,
      responsavelCep: merchant.responsibleZipCode,
      responsavelEstado: merchant.responsibleState,
      responsavelCidade: merchant.responsibleCity,
      responsavelEndereco: merchant.responsibleAddress,
      responsavelNumero: merchant.responsibleNumber,
      responsavelBairro: merchant.responsibleNeighborhood,
      responsavelComplemento: merchant.responsibleComplement,
      idExterno: merchant.externalId,
      tipoConta: merchant.accountType,
      validarIp: merchant.validateIp,
      ips: merchant.ipAddresses ? merchant.ipAddresses.join(', ') : ''
    };
  }

  getCreatedAt() {
    return this.data?.createdAt;
  }

  getUpdatedAt() {
    return this.data?.updatedAt;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private service: PartnerService,
    public override fb: FormBuilder,
    private dialog: MatDialog,
    private toastService: ToastService,
    private location: Location,
    public permissionService: PermissionService,
    private merchantService: MerchantService,
    private merchantValidator: MerchantValidatorService,
  ) {
    super(fb);
    this.typeCes = route.snapshot.data['type'];
    this.title = `${this.typeCes} ${this.service.title.toUpperCase()}`;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadStates();

    this.legalPersonalitySub = this.form
      .get('legalPersonality')
      ?.valueChanges.subscribe((data) => {
        if (data === LegalPersonalityEnum.PESSOA_FISICA)
          this.setToFisicPerson();
        else this.setToLegalPerson();
      });

    this.form.get('isMerchant')
      ?.valueChanges.subscribe((data) => {
        this.showModalMerchant = data;
        if(data) this.addValidators('addressNumber', [Validators.required]);
         else this.removeValidators('addressNumber', [Validators.required]);
        
        // if (data && !this.resultModal && !this.isLoadingMerchantData) {
        //   setTimeout(() => this.modalMerchant(), 100);
        // }

        if (!data) {
          this.resultModal = null as any;
        }
      });

    this.setToFisicPerson();

    this.setupCommissionFieldListeners();
  }

  ngAfterViewInit(): void {
    this.route.params.subscribe((params) => {
      if (params['id'])
        this.service.get(params['id']).subscribe((data: any) => {
          this.data = data;
          this.setFields();

          setTimeout(() => {
            this.cdr.detectChanges();
          });
        });
    });
  }

  private setToFisicPerson() {
    this.removeValidators('companyName', [
      Validators.required,
      Validators.min(4),
    ]);
    this.removeValidators('cnpj', [
      Validators.required,
      Validators.minLength(14),
    ]);

    this.addValidators('name', [Validators.required, Validators.min(4)]);
    this.addValidators('cpf', [Validators.required, Validators.minLength(11)]);
    this.addValidators('generalRegistration', [
      Validators.required,
      Validators.pattern(/\d+/),
    ]);
  }

  private setToLegalPerson() {
    this.addValidators('companyName', [Validators.required, Validators.min(4)]);
    this.addValidators('cnpj', [Validators.required, Validators.minLength(14)]);

    this.removeValidators('name', [
      Validators.required,
      Validators.minLength(4),
    ]);
    this.removeValidators('cpf', [
      Validators.required,
      Validators.minLength(11),
    ]);
    this.removeValidators('generalRegistration', [
      Validators.required,
      Validators.pattern(/\d+/),
    ]);
    this.addValidators('birthday', [Validators.required]);
  }

  private addValidators(name: any, fn: any) {
    const control = this.form.get(name);
    control?.addValidators(fn);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  private removeValidators(name: any, fn: any) {
    const control = this.form.get(name);
    control?.removeValidators(fn);
    control?.updateValueAndValidity({ emitEvent: false });
  }


  ngOnDestroy() {
    this.legalPersonalitySub?.unsubscribe();
    this.isConsultantSub?.unsubscribe();
    this.isPartnerSub?.unsubscribe();
    this.isRepresentativeSub?.unsubscribe();
    this.isCounterSub?.unsubscribe();
  }

  public getCitiesByUf(uf?: any): Observable<any> {
    const state = uf || this.form.get('state')?.value;
    this.form.get('city')?.reset();

    return this.service.getCitiesByUf(state);
  }

  openDialogBusinessHours() {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: {
        minWidth: '600px',
        maxWidth: '800px',
        minHeight: '420px',
        maxHeight: '900px',
        width: 'auto',
        height: 'auto',
      },
      component: BusinessHoursComponent,
      data: {
        formGroup: this.form,
        typeCes: this.typeCes,
        config: this.config,
      },
    });
  }

  public onChangeCep() {
    const value = this.form.get('cep')?.value;
    if (value && value.length === 8) {
      this.service
        .getCepInfo(value)
        .pipe(take(1))
        .subscribe((result: IPartner) => {
          this.getCitiesByUf(result.state)
            .pipe(take(1))
            .subscribe((resp: any) => {
              this.form.get('city')?.reset();
              this.getFieldConfig('city').options = resp.map(
                (v: any) => v.nome
              );

              this.form.patchValue({
                ...result,
              });
            });
        });
    }
  }


  public async save() {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();

      if (formValue.isMerchant && !this.resultModal) {
        this.toastService.showWarn('Por favor, preencha os dados do comerciante antes de salvar.');
        return;
      }

      let legalPersonality = LegalPersonalityIdEnum[this.form.get('legalPersonality')?.getRawValue() as LegalPersonalityEnum];

      if (this.form.get('id')?.value) {
        if (formValue.isMerchant && this.resultModal && !this.existingMerchant ) {
          const validationErrors = this.merchantValidator.validate(this.resultModal, formValue);

          if (validationErrors.length > 0) {
            const errorMessages = validationErrors.slice(0, 5).map(e => `• ${e.field}: ${e.message}`).join('\n');
            const moreErrors = validationErrors.length > 5 ? `\n... e mais ${validationErrors.length - 5} erro(s)` : '';

            this.toastService.showError(`Erros de validação do comerciante:\n${errorMessages}${moreErrors}\n\nPor favor, edite os dados do comerciante e corrija os erros.`);
            return;
          }

          try {
            await this.saveMerchante({ ...formValue, partnerId: this.form.get('id')?.value });
          } catch (error) {
            return;
          }
        }

        this.service
          .updateWithFormData(
            this.form.get('id')?.getRawValue(),
            await new FormDataUpdate().build({
              ...this.form.getRawValue(),
              legalPersonality,
              consultant: this.consultantSelected?.id,
              partner: this.partnerSelected?.id,
              representative: this.representativeSelected?.id
            })
          )
          .subscribe((data: any) => {
            this.router.navigate(['partners/show', this.data.id]);
            this.toastService.showSuccess(MessagesEnum.SUCCESS_UPDATED);
          });
      } else {
        this.service
          .createWithFormData(
            await new FormDataCreate().build({
              ...this.form.getRawValue(),
              legalPersonality,
              consultant: this.consultantSelected?.id,
              partner: this.partnerSelected?.id,
              representative: this.representativeSelected?.id
            })
          )
          .subscribe(async (data: any) => {
            if (formValue.isMerchant && this.resultModal) {
              const validationErrors = this.merchantValidator.validate(this.resultModal, formValue);

              if (validationErrors.length > 0) {
                const errorMessages = validationErrors.slice(0, 5).map(e => `• ${e.field}: ${e.message}`).join('\n');
                const moreErrors = validationErrors.length > 5 ? `\n... e mais ${validationErrors.length - 5} erro(s)` : '';

                this.toastService.showWarn(`Parceiro criado, mas houve erros no comerciante:\n${errorMessages}${moreErrors}`);
                this.router.navigate(['partners/show', data.id]);
                return;
              }

              try {
                await this.saveMerchante({ ...formValue, partnerId: data.id });
                this.toastService.showSuccess('Parceiro e comerciante criados com sucesso!');
              } catch (error) {
                this.toastService.showWarn(`Parceiro criado, mas houve erro ao criar o comerciante.`);
              }
            } else {
              this.toastService.showSuccess(MessagesEnum.SUCCESS_CREATED);
            }

            this.router.navigate(['partners/show', data.id]);
          });
      }
    }
  }

  private async saveMerchante(form: any): Promise<void> {
    const formatted = this.formatFormDataForAPI(form, this.resultModal);
    const request = this.mapToRequest(formatted);

    try {
      const response = await firstValueFrom(
        this.merchantService.createMerchant(request).pipe(
          tap(() => {
            console.log("COMERCIANTE CADASTRADO COM SUCESSO");
          })
        )
      );
    } catch (error) {
      console.error("Erro ao salvar o comerciante:", error);
      this.toastService.showError(this.normalizeApiError(error));
      throw error;
    } finally {

    }
  }
  public edit() {
    this.router.navigate(['partners/edit', this.data.id]);
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  onChangeCnpj() {
    const value = this.form.get('cnpj')?.value;

    if (value.length < 14) return;

    this.service
      .getCnpjInfo(value)
      .pipe(take(1))
      .subscribe({
        next: (result: IPartner) => {
          this.form.patchValue({ ...result });
        }
      });
  }

  get legalPersonalityEnum() {
    return LegalPersonalityEnum;
  }

  public getLegalPersonality(type: LegalPersonalityEnum) {
    return type === this.form.get('legalPersonality')?.value;
  }

  public loadStates() {
    this.service
      .getStates()
      .pipe(take(1))
      .subscribe((resp) => {
        this.getFieldConfig('state').options = [
          '',
          ...resp.map((r: any) => r.sigla).sort(),
        ];
        this.form.get('state')?.setValue('');
      });
  }

  public filterCities(query: string) {
    const state = this.form.get('state')?.value;
    if (!state || state == 'Todos') {
      this.form.get('city')?.reset();
      this.toastService.showWarn('Selecione o estado para filtrar cidades!');
      return;
    }
    of(query.toLowerCase())
      .pipe(
        filter((text) => text.length > 2),
        debounceTime(400),
        distinctUntilChanged(),
        map((text) =>
          this.getFieldConfig('city').options.filter((option: any) =>
            typeof option === 'object'
              ? option?.name.toLowerCase().includes(text)
              : option.toLowerCase().includes(text)
          )
        )
      )
      .subscribe((filterResult) => {
        this.filteredCities = filterResult as any;
        this.cdr.markForCheck();
      });
  }

  public onChangeState(data: string) {
    this.getCitiesByUf(data).subscribe((resp) => {
      this.getFieldConfig('city').options = resp.map((v: any) => v.nome);
      this.cdr.detectChanges();
    });
  }

  public selectConsultant($event: any) {
    this.consultantSelected = $event;
  }

  public selectPartner($event: any) {
    this.partnerSelected = $event;
  }

  public selectRepresentative($event: any) {
    this.representativeSelected = $event;
  }

  private setupCommissionFieldListeners(): void {
    this.isConsultantSub = this.form
      .get('isConsultant')
      ?.valueChanges.subscribe((isChecked) => {
        this.updateCommissionFieldState('consultantCommission', isChecked);
      });

    this.isPartnerSub = this.form
      .get('isPartner')
      ?.valueChanges.subscribe((isChecked) => {
        this.updateCommissionFieldState('partnerCommission', isChecked);

        if (!isChecked && !this.form.get('isRepresentative')?.value) {
          this.form.get('consultant')?.setValue(null);
          this.consultantSelected = null;
        }
      });

    this.isRepresentativeSub = this.form
      .get('isRepresentative')
      ?.valueChanges.subscribe((isChecked) => {
        this.updateCommissionFieldState('representativeCommission', isChecked);
        if (!isChecked) {
          this.form.get('partner')?.setValue(null);
          this.partnerSelected = null;
          this.form.get('representative')?.setValue(null);
          this.representativeSelected = null;

          if (!this.form.get('isPartner')?.value) {
            this.form.get('consultant')?.setValue(null);
            this.consultantSelected = null;
          }
        }
      });

    this.isCounterSub = this.form
      .get('isCounter')
      ?.valueChanges.subscribe((isChecked) => {
        this.updateCommissionFieldState('counterCommission', isChecked);
      });

    this.updateCommissionFieldState('consultantCommission', this.form.get('isConsultant')?.value);
    this.updateCommissionFieldState('partnerCommission', this.form.get('isPartner')?.value);
    this.updateCommissionFieldState('representativeCommission', this.form.get('isRepresentative')?.value);
    this.updateCommissionFieldState('counterCommission', this.form.get('isCounter')?.value);
  }

  private updateCommissionFieldState(fieldName: string, isEnabled: boolean): void {
    const field = this.form.get(fieldName);

    if (field) {
      if (isEnabled) {
        field.enable();
      } else {
        field.disable();
        field.setValue(null); 
      }
    }
  }

  onBlur(event: any): void {
    if (event.field.onBlur === 'checkCustomerDocument' && this.typeCes === TypeCesEnum.NEW) {
      const fieldValue = event.value;

      if (fieldValue) {
        const documentValue = fieldValue.replace(/[^\d]/g, '');

        if (documentValue.length >= 11) {
          this.service.checkExistingCustomer(documentValue)
            .pipe(take(1))
            .subscribe({
              next: (customer) => {
                if (customer) {
                  const dialogRef = this.dialog.open(CustomerDataDialogComponent, {
                    width: '400px',
                    data: { customer }
                  });

                  dialogRef.afterClosed().subscribe(result => {
                    if (result) {
                      this.form.patchValue({
                        name: customer.name,
                        companyName: customer.companyName,
                        tradingName: customer.tradingName,
                        document: customer.cpf || customer.cnpj,
                        stateRegistration: customer.stateRegistration,
                        municipalRegistration: customer.municipalRegistration,
                        generalRegistration: customer.generalRegistration,

                        phone: customer.phone,
                        cellPhone: customer.cellPhone,
                        email: customer.email,
                        webSite: customer.webSite,

                        cep: customer.cep,
                        address: customer.address,
                        addressComplement: customer.addressComplement,
                        addressNumber: customer.addressNumber,
                        neighborhood: customer.neighborhood,
                        state: customer.state,
                        city: customer.city,
                        country: customer.country,
                        addressObservation: customer.addressObservation,

                        image: customer.image,
                        birthday: customer.birthday,
                        mainActivity: customer.mainActivity,
                        consultantCommission: customer.consultantCommission,
                        partnerCommission: customer.partnerCommission,
                        representativeCommission: customer.representativeCommission,
                        counterCommission: customer.counterCommission,
                        unitCommission: customer.unitCommission,
                        regime: getRegimeTypeDescriptionByValue(customer.regime),
                        fiscalEnvironment: customer.fiscalEnvironment,
                      });

                      if (customer.state) {
                        this.onChangeState(customer.state);
                        setTimeout(() => {
                          this.form.get('city')?.setValue(customer.city);
                        }, 500);
                      }
                    }
                  });
                }
              },
              error: (error) => {
                console.log('No customer found for document:', documentValue);
              }
            });
        }
      }
    }
  }

  protected modalMerchant() {
    this.addValidators('addressNumber', [Validators.required]);
    if(this.form.invalid) return;
    const formValue = this.form.getRawValue();

    const dataToPass = {
      ...formValue,
      merchantData: this.resultModal, 
      typeCes: this.existingMerchant ? this.typeCesEnum.SHOW : this.typeCesEnum.NEW
    };

    const dialogRef = this.dialog.open(DialogMerchantComponent, {
      width: '1200px',
      height: '900px',
      disableClose: false,
      data: dataToPass
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.resultModal = result;
      }
    });
  }

  private mapToRequest(formatted: any): ICreateMerchantRequest {
    const isPessoaFisica = formatted.legalPersonality === LegalPersonalityEnum.PESSOA_FISICA;

    return {
      geral: {
        tipoPessoa: isPessoaFisica ? 'pf' : 'pj',
        numeroCnpj: isPessoaFisica ? formatted.cpf : formatted.cnpj,
        nomeComerciante: isPessoaFisica ? formatted.name : formatted.companyName, 
        nomeSocial: isPessoaFisica ? (formatted.tradingName || formatted.name) : formatted.tradingName,
        dataNascimento: formatted.birthday,
        inscricaoEstadual: formatted.stateRegistration || null,
        inscricaoMunicipal: formatted.municipalRegistration || null,
        email: formatted.email,
        financeiro: {
          chavePix: formatted.chavePix || undefined,
          celularFinanceiro: formatted.celularFinanceiro,
          emailFinanceiro: formatted.emailFinanceiro
        },
        endereco: {
          cep: formatted.cep,
          estado: formatted.state,
          cidade: formatted.city,
          endereco: formatted.address,
          numero: formatted.addressNumber,
          bairro: formatted.neighborhood,
          complemento: formatted.addressComplement || null
        }
      },
      responsavel: {
        nome: formatted.responsavelNome,
        cpf: formatted.responsavelCpf,
        rg: formatted.responsavelRg,
        nacionalidade: formatted.responsavelNacionalidade,
        celular: formatted.responsavelCelular,
        email: formatted.responsavelEmail,
        dataNascimento: formatted.responsavelDataNascimento.toString(),
        endereco: {
          cep: formatted.responsavelCep,
          estado: formatted.responsavelEstado,
          cidade: formatted.responsavelCidade,
          endereco: formatted.responsavelEndereco,
          numero: formatted.responsavelNumero,
          bairro: formatted.responsavelBairro,
          complemento: formatted.responsavelComplemento
        }
      },
      idExterno: formatted.idExterno,
      tipoConta: formatted.tipoConta,
      configuracao: {
        validarIp: formatted.validarIp,
        ip: formatted.validarIp && formatted.ips ? formatted.ips.split(',').map((ip: string) => ip.trim()) : []
      },
      partnerId: formatted.partnerId || null
    };
  }

  private formatFormDataForAPI(formValue: any, formResponsavel: IMerchantPartnersFormData): any {
    return {
      ...formValue,
      birthday: formatDateForAPI(formValue.birthday),
      cnpj: formValue.cnpj?.replace(/\D/g, '') || '',
      cpf: formValue.cpf?.replace(/\D/g, '') || '',
      cep: formValue.cep?.replace(/\D/g, '') || '',
      partnerId: formValue.partnerId || null, 

      chavePix: formResponsavel.chavePix,
      celularFinanceiro: formatPhoneForAPI(formResponsavel.celularFinanceiro),
      emailFinanceiro: formResponsavel.emailFinanceiro,

      responsavelNome: formResponsavel.responsavelNome,
      responsavelCpf: formResponsavel.responsavelCpf?.replace(/\D/g, '') || '',
      responsavelRg: formResponsavel.responsavelRg,
      responsavelNacionalidade: formResponsavel.responsavelNacionalidade,
      responsavelCelular: formatPhoneInternational(formResponsavel.responsavelCelular),
      responsavelEmail: formResponsavel.responsavelEmail,
      responsavelDataNascimento: formatDateForAPI(formResponsavel.responsavelDataNascimento),

      responsavelCep: formResponsavel.responsavelCep?.replace(/\D/g, '') || '',
      responsavelEstado: formResponsavel.responsavelEstado,
      responsavelCidade: formResponsavel.responsavelCidade,
      responsavelEndereco: formResponsavel.responsavelEndereco,
      responsavelNumero: formResponsavel.responsavelNumero,
      responsavelBairro: formResponsavel.responsavelBairro,
      responsavelComplemento: formResponsavel.responsavelComplemento,

      idExterno: formResponsavel.idExterno,
      tipoConta: formResponsavel.tipoConta,
      validarIp: formResponsavel.validarIp,
      ips: formResponsavel.ips
    };
  }

  private normalizeApiError(error: any): string {
    if (!error) return 'Erro ao cadastrar comerciante';

    if(typeof error === 'string'){
      return error;
    }
    const status = error.status;

    if (status === 400) {
      const msg: string | undefined = error?.error?.message;
      if (msg?.includes('|')) {
        const list = msg.split(', ').map((e: string) => e.split('|')[1]?.trim() || e);
        return `Erros da API Canvi:\n${list.join('\n')}`;
      }
      if (msg) return msg;

      const arr = error?.error?.errors;
      if (Array.isArray(arr)) {
        const list = arr.map((e: string) => e.split('|')[1]?.trim() || e);
        return `Erros de validação:\n${list.join('\n')}`;
      }
      return 'Erro de validação (400).';
    }

    if (status === 409) {
      return error?.error?.message || 'Comerciante já cadastrado com este CNPJ/CPF ou ID externo';
    }

    if (status === 422) return 'Dados inválidos. Verifique os campos e tente novamente.';
    if (status >= 500) return 'Erro interno do servidor. Tente novamente mais tarde.';
    return 'Erro ao cadastrar comerciante';
  }

}
